/**
 * ExpandToggle.jsx — THE CANONICAL expand/collapse glyph for Granullo.
 *
 * DOCTRINE:
 *   The "+" is rendered in the hand-sketched brush font (Permanent Marker)
 *   and surrounded by a hand-drawn scarlet scribble-circle that draws itself
 *   in on expansion. On open, the "+" rotates 45° to "×".
 *
 *   Use this anywhere a card / row / panel can be expanded — FAQs, case study
 *   cards, rule chains, registry rows, evidence cards, etc.
 *
 * USAGE:
 *   <ExpandToggle open={isOpen} size="md" />
 *   <button onClick={() => setOpen(!open)}>
 *     <span>Show details</span>
 *     <ExpandToggle open={open} />
 *   </button>
 *
 * SIZES:
 *   xs  → 20px   (inline micro-toggles inside dense tables)
 *   sm  → 28px   (row-level toggles inside cards)
 *   md  → 36px   (default accordion items / FAQs)
 *   lg  → 48px   (hero-level expandable panels)
 */
import React from 'react';
import { motion } from 'framer-motion';
import './expand-toggle.css';

const SIZE_PX = { xs: 20, sm: 28, md: 36, lg: 48 };

export function ExpandToggle({
  open = false,
  size = 'md',
  tone = 'scarlet',
  testId = 'expand-toggle',
  className = '',
}) {
  const px = SIZE_PX[size] ?? SIZE_PX.md;

  return (
    <span
      className={`gex-toggle gex-tone-${tone} ${open ? 'is-open' : ''} ${className}`}
      style={{ width: px, height: px }}
      data-testid={testId}
      data-open={open}
      aria-hidden
    >
      {/* Hand-drawn scribble circle — draws in via pathLength */}
      <svg className="gex-scribble" viewBox="0 0 48 48" fill="none">
        <motion.path
          d="M24 4 C36 3, 45 10, 44 22 C43 34, 35 45, 23 44 C11 43, 3 35, 4 23 C5 12, 13 5, 24 4"
          strokeWidth={2.4}
          strokeLinecap="round"
          fill="none"
          initial={false}
          animate={open ? { pathLength: 1, opacity: 1 } : { pathLength: 0, opacity: 0 }}
          transition={{ duration: 0.28, ease: [0.4, 0, 0.2, 1] }}
        />
      </svg>

      {/* "+" glyph — hand-sketched Permanent Marker font */}
      <motion.span
        className="gex-plus"
        animate={{ rotate: open ? 45 : 0 }}
        transition={{ duration: 0.22, ease: [0.25, 0.1, 0.25, 1] }}
      >
        +
      </motion.span>
    </span>
  );
}

export default ExpandToggle;
